import { cn } from '@forest/shared'
import { useStore } from '@nanostores/react'
import { useRive, useViewModelInstanceBoolean } from '@rive-app/react-canvas'
import { AnimatePresence } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import rivGeneral from '~/assets/rive/general.riv'
import { getLocalizedMessage } from '~/lib/l10n'
import { $config } from '~/store/config'
import { motion } from '~/lib/motion'
import type { ItemContent } from '~/types/delivery'
import { ClaimableBubbles } from './claimable-bubbles'
import { FinalRewardFrameGeneral } from './graphics/final-reward-frame-general'
import { FinalRewardFramePaid } from './graphics/final-reward-frame-paid'
import { RewardFrameGeneral } from './graphics/reward-frame-general'
import { RewardFramePaid } from './graphics/reward-frame-paid'
import { Spinner } from '@forest/shared/react'

interface Props {
  status: 'pending' | 'claimable' | 'claimed'
  in: 'paid-track' | 'general-track'
  iconUrl: string
  reward: {
    type: ItemContent['type']
    amount: number
  }
  isLast: boolean
  locked?: boolean
  unlockAnimationToken?: number
  /** Resolved against the web string catalog; the badge hides on a miss. */
  badgeTitleKey?: string | null
  onClick?: () => void
  onLockedClick?: () => void
}

export function RewardItem(props: Props) {
  const config = useStore($config)
  const badgeLabel = getLocalizedMessage(props.badgeTitleKey ?? null)
  const showLabel = props.reward.type === 'coin' || props.reward.type === 'gem'
  const isClaimed = props.status === 'claimed'
  const isClaimable = props.status === 'claimable' && !props.locked
  const [isLockVisible, setIsLockVisible] = useState(Boolean(props.locked))
  const [isUnlockAnimating, setIsUnlockAnimating] = useState(false)
  const lastUnlockAnimationTokenRef = useRef(props.unlockAnimationToken)

  useEffect(() => {
    const hasNewUnlockAnimation =
      props.unlockAnimationToken != null &&
      props.unlockAnimationToken !== lastUnlockAnimationTokenRef.current

    if (props.locked) {
      setIsLockVisible(true)
      setIsUnlockAnimating(false)
      return
    }

    if (hasNewUnlockAnimation) {
      lastUnlockAnimationTokenRef.current = props.unlockAnimationToken
      setIsLockVisible(true)
      setIsUnlockAnimating(true)
      return
    }

    setIsUnlockAnimating(false)
    setIsLockVisible(false)
  }, [props.locked, props.unlockAnimationToken])

  const [isImgLoaded, setIsImgLoaded] = useState(false)

  if (!config) return null

  return (
    <div
      className="relative size-[104px]"
      onClick={
        props.isLast || isClaimable
          ? props.onClick
          : props.locked
            ? props.onLockedClick
            : undefined
      }
    >
      {props.in === 'paid-track' ? (
        props.isLast ? (
          <FinalRewardFramePaid colors={config.paid_final_reward_background} />
        ) : (
          <RewardFramePaid
            active={props.status === 'claimable'}
            strokeColors={config.paid_reward_frame_color}
            backgroundColor={config.paid_reward_background}
          />
        )
      ) : props.isLast ? (
        <FinalRewardFrameGeneral colors={config.free_final_reward_background} />
      ) : (
        <RewardFrameGeneral active={props.status === 'claimable'} />
      )}

      <motion.div
        className="absolute left-1/2 top-[6px] size-20 -translate-x-1/2"
        initial={{ opacity: 0 }}
        animate={{ opacity: isImgLoaded ? 1 : 0 }}
      >
        <img
          className={cn((isClaimed || props.locked) && 'opacity-30')}
          src={props.iconUrl}
          onLoad={() => setIsImgLoaded(true)}
          loading="lazy"
        />
      </motion.div>

      <AnimatePresence initial={false}>
        {!isImgLoaded && (
          <motion.div
            key="img-spinner"
            exit={{ opacity: 0 }}
            className="absolute left-1/2 top-8 size-9 -translate-x-1/2"
          >
            <Spinner className="size-full" />
          </motion.div>
        )}
      </AnimatePresence>

      {showLabel && (
        <Label
          type={props.reward.type as 'gem' | 'coin'}
          amount={props.reward.amount}
          className={isClaimed || props.locked ? 'opacity-30' : ''}
        />
      )}

      {isClaimable && <ClaimableBubbles />}

      {isClaimed && (
        <div className="absolute inset-0 flex items-center justify-center">
          <svg
            width="31"
            height="27"
            viewBox="0 0 31 27"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M26.2818 4.2572C27.7046 5.42539 27.911 7.52582 26.7428 8.94862L15.7955 22.2819C15.177 23.0352 14.2597 23.4801 13.2851 23.4993C12.3106 23.5186 11.3765 23.1103 10.7287 22.382L4.34272 15.2026C3.11922 13.827 3.24247 11.7201 4.618 10.4966C5.99354 9.27309 8.10047 9.39634 9.32398 10.7719L13.1179 15.0372L21.5904 4.7182C22.7586 3.2954 24.859 3.089 26.2818 4.2572Z"
              fill="white"
            />
            <path
              d="M20.2383 3.60742C22.0198 1.43799 25.2229 1.12291 27.3926 2.9043C29.5624 4.6858 29.8772 7.88979 28.0957 10.0596L17.1484 23.3926C16.2052 24.5414 14.8055 25.2197 13.3193 25.249C11.8333 25.2783 10.4087 24.6555 9.4209 23.5449L3.03516 16.3652C1.16956 14.2676 1.35759 11.0553 3.45508 9.18945C5.55274 7.32364 8.76598 7.51079 10.6318 9.6084L13.0645 12.3438L20.2383 3.60742Z"
              stroke="white"
              strokeOpacity="0.3"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}

      {isLockVisible && (
        <Lock
          isUnlocked={isUnlockAnimating}
          onUnlockComplete={() => {
            setIsUnlockAnimating(false)
            setIsLockVisible(false)
          }}
        />
      )}

      {badgeLabel && (
        <div
          className="absolute -bottom-2 left-1/2 flex h-5 -translate-x-1/2 items-center text-nowrap rounded-3xl px-2 text-[10px] font-semibold text-[#333]"
          style={{
            background: 'linear-gradient(180deg, #FFF 0%, #E3E3E3 100%)',
            boxShadow: '0 0 4px 0 #FFFFFF99',
          }}
        >
          {badgeLabel}
        </div>
      )}
    </div>
  )
}

function Label(props: {
  type: 'gem' | 'coin'
  amount: number
  className?: string
}) {
  return (
    <>
      <span
        className={cn(
          'absolute left-[3px] right-[3px] top-[75px] text-center text-sm font-extrabold',
          props.className,
        )}
        style={{
          WebkitTextStrokeColor: '#F9F9F9',
          WebkitTextStrokeWidth: 3,
          color: 'transparent',
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        x{props.amount}
      </span>
      <span
        className={cn(
          'absolute left-[3px] right-[3px] top-[75px] text-center text-sm font-extrabold',
          props.className,
        )}
        style={{
          background: labelColorByRewardType[props.type],
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          overflow: 'hidden',
        }}
      >
        x{props.amount}
      </span>
    </>
  )
}

const labelColorByRewardType = {
  coin: 'linear-gradient(10deg, #EB7702 7.27%, #FED345 83.57%)',
  gem: 'linear-gradient(10deg, #4C57B3 -4.93%, #45C6FE 104.93%)',
}

const LOCK_UNLOCK_FALLBACK_MS = 1000

function Lock(props: { isUnlocked: boolean; onUnlockComplete: () => void }) {
  const hasTriggeredUnlockRef = useRef(false)
  const hasCompletedUnlockRef = useRef(false)
  const unlockTimeoutRef = useRef<number | null>(null)
  const onUnlockCompleteRef = useRef(props.onUnlockComplete)

  useEffect(() => {
    onUnlockCompleteRef.current = props.onUnlockComplete
  }, [props.onUnlockComplete])

  const { RiveComponent, rive } = useRive({
    src: rivGeneral,
    autoplay: true,
    stateMachines: ['State Machine 1'],
    artboard: 'lock',
    autoBind: true,
    onStateChange: ({ data }) => {
      if (!hasTriggeredUnlockRef.current || hasCompletedUnlockRef.current) {
        return
      }

      if (
        Array.isArray(data) &&
        data.some(
          (state) => typeof state === 'string' && state.startsWith('idle'),
        )
      ) {
        hasCompletedUnlockRef.current = true
        if (unlockTimeoutRef.current !== null) {
          window.clearTimeout(unlockTimeoutRef.current)
          unlockTimeoutRef.current = null
        }
        onUnlockCompleteRef.current()
      }
    },
  })

  const isUnlockedInput = useViewModelInstanceBoolean(
    'booleanProperty',
    rive?.viewModelInstance,
  )

  useEffect(() => {
    if (!props.isUnlocked || hasTriggeredUnlockRef.current) return

    hasTriggeredUnlockRef.current = true
    isUnlockedInput.setValue(true)

    unlockTimeoutRef.current = window.setTimeout(() => {
      if (hasCompletedUnlockRef.current) return

      hasCompletedUnlockRef.current = true
      unlockTimeoutRef.current = null
      onUnlockCompleteRef.current()
    }, LOCK_UNLOCK_FALLBACK_MS)

    return () => {
      if (unlockTimeoutRef.current !== null) {
        window.clearTimeout(unlockTimeoutRef.current)
        unlockTimeoutRef.current = null
      }
    }
  }, [isUnlockedInput, props.isUnlocked])

  useEffect(() => {
    return () => {
      if (unlockTimeoutRef.current !== null) {
        window.clearTimeout(unlockTimeoutRef.current)
      }
    }
  }, [])

  return (
    <RiveComponent className="pointer-events-none absolute left-0 top-0 h-[104px] w-[113px]" />
  )
}
